import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AuthConnectService,
  PLATFORM_ACTIONS,
  PanAppState,
  PlatformEnum,
  PlatformService,
  Tracking,
} from '@panamax/app-state';
import {
  MyOrdersStoreService,
  MY_ORDERS_ACTIONS,
  OrderItemDTOService,
  NEXT_DELIVERY_ACTIONS,
  CART_ACTIONS,
  OrderStoreService,
  orderHeaderSelectors,
  ORDER_PUNCHOUT_ACTIONS,
  partnerSelectors,
  OrderStatus,
  SHIP_TO_ACTIONS,
  OrderItem,
  ORDER_REVIEW_ACTIONS,
} from '@usf/ngrx-order';
import { EMPTY } from 'rxjs/internal/observable/empty';
import {
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { HomeService } from 'src/app/home/services/home.service';
import { MessageTypeEnum } from 'src/app/ngrx-message/constants/messageTypeEnum';
import { Message } from 'src/app/ngrx-message/models/client/message';
import { upsertMessage } from 'src/app/ngrx-message/store/actions/message/message.action';
import { OrderService } from '../services/order.service';
import { v4 as uuidv4 } from 'uuid';
import { MessageHandlerService } from 'src/app/shared/services/message/message-handler.service';
import { ToastMessages } from 'src/app/shared/constants/toast-messages.enum';
import { CONVERT_TO_WILL_CALL_TRACING_ACTIONS } from '../pages/order-submitted/will-call/tracing/convert-to-will-call-tracing.actions';
import { ConvertToWillCallTracingMessages } from '../pages/order-submitted/will-call/tracing/convert-to-will-call-tracing.constants';
import { Store } from '@ngrx/store';
import { productWithAlternativesSelector } from 'src/app/shared/selectors/product.selectors';
import { SharedTrackingService } from 'src/app/shared/services/analytics/shared-tracking.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { MAX_TIME_IN_BACKGROUND } from '@shared/constants/lifecycle-constants';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';
import { ModalService } from '@shared/services/modal/modal.service';
import { ModalController } from '@ionic/angular';
import { combineLatest, from, of } from 'rxjs';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { AddToOrderModalMobileComponent } from '@shared/components/add-to-order-modal/add-to-order-modal-mobile/add-to-order-modal-mobile.component';
import { AddToOrderModalDesktopTabletComponent } from '@shared/components/add-to-order-modal/add-to-order-modal-desktop-tablet/add-to-order-modal-desktop-tablet.component';
import { ORDER_ACTIONS } from '@order/actions/order-actions';
import { OrderAnalyticsService } from '@order/tracking/analytics/order-analytics.service';
import { selectedCustomer } from '@app/ngrx-customer/store/selectors';
import { OrderPrintService } from '@shared/services/file-download/order-print.service';
import { printOrderAnalytics } from '@order/tracking/analytics/factories/print-order-analytics.factory';
import { ORDER_ANALYTICS_ACTIONS } from '@order/tracking/analytics/order-analytics.actions';
import { OrderDownloadService } from '@shared/services/file-download/order-download.service';
import { TranslateService } from '@ngx-translate/core';
import { OrderConversionService } from '@order/services/order-conversion.service';
import { OrderShareViaEmailService } from '@order/services/order-share-via-email.service';
import { OrderShareViaNativeService } from '@order/services/order-share-via-native.service';
import { OrderShareService } from '@order/services/order-share.service';
import { OrderSubmittedAnalyticsService } from '../pages/order-submitted/tracking/analytics/order-submitted-analytics.service';
import { OrderSubmittedTracingService } from '../pages/order-submitted/tracking/tracing/order-submitted-tracing.service';
import { OrderTracingService } from '../tracking/tracing/order-tracing.service';

@Injectable({
  providedIn: 'root',
})
export class ClientOrderEffects {
  //TODO: This flag should be moved to a service class
  private modalOpen = false;
  private orderShareService: OrderShareService;

  clientHandleUpdateSubmittedOrderError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.updateSubmittedOrderAsync),
      map(payload => {
        const actionsToDispatch = [];
        for (const orderTransaction of payload.orderTransactions) {
          if (
            !!orderTransaction.errorDetails &&
            orderTransaction?.orderStatus === OrderStatus.CANCELLED
          ) {
            this.toastService.presentToastMsg(
              this.translateService.instant(
                'i18n.orderPage.orderCancelledErrorMsg',
              ),
              'red-toast',
              MessageTypeEnum.warning,
              [],
            );
          }
        }
        return actionsToDispatch;
      }),
      switchMap(actions => actions),
    ),
  );

  clientCreateNewOrderMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.createNewCartMessage),
      switchMap(action => [upsertMessage({ message: action.message })]),
    ),
  );

  clientClearIsPartnerModifyingOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CART_ACTIONS.createNewCart,
          CART_ACTIONS.cancelSubmittedCartSuccess,
          CART_ACTIONS.loadCartIntoContext,
        ),
        tap(() => sessionStorage.removeItem('isPartnerModifyingOrder')),
      ),
    { dispatch: false },
  );

  cancelOrderAsync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.cancelOrderAsync),
      map(payload => {
        const actionsToDispatch = [];
        for (const orderTransaction of payload.orderTransactions) {
          if (!!orderTransaction.errorDetails) {
            const message = {
              id: this.uuid(),
              stack: null,
              watermark: new Date().toUTCString(),
              read: false,
              type: MessageTypeEnum.warning,
              display: 'This order cannot be edited at this time.',
              toast: true,
              sticky: false,
            } as Message;
            actionsToDispatch.push(upsertMessage({ message }));
          }
        }
        return actionsToDispatch;
      }),
      switchMap(actions => actions),
    ),
  );

  clientRealtimeTmUserApiCallSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MY_ORDERS_ACTIONS.getOrderDetailsSuccess),
        withLatestFrom(this.homeService.loadCustomer$()),
        tap(([res, customer]) => {
          this.messageHandlerService.dismissSpinnerModal();
          const orderId =
            `${res?.order?.orderHeader?.customerNumber}:` +
            `${res?.order?.orderHeader?.departmentNumber}:` +
            `${res?.order?.orderHeader?.orderId}`;
          this.myOrdersStoreService
            .getOrderWithId(orderId)
            .pipe(take(1))
            .subscribe(order => {
              if (order?.orderHeader?.errorDetails) {
                return;
              } else {
                if (res?.isEditFlow) {
                  this.myOrdersStoreService
                    .getOrderWithId(orderId)
                    .pipe(take(1))
                    .subscribe(order => {
                      const path = ['order'];
                      const deviceTypePath = this.platformService?.isTouch.value
                        ? path.unshift('touch')
                        : path.unshift('desktop');
                      this.router.navigate(path);
                      this.orderService.makeOrderCurrentOrder(order, customer);
                    });
                } else {
                  this.orderService.loadProductsForOrder(order);
                }
              }
              return;
            });
        }),
      ),
    { dispatch: false },
  );

  clientRealtimeTmUserApiCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getOrderDetailsFailure),
      map(_ => {
        const actionsToDispatch = [];
        const message = {
          id: this.uuid(),
          stack: null,
          watermark: new Date().toUTCString(),
          read: false,
          type: MessageTypeEnum.error,
          display:
            "We're sorry but the details of this order are not available at this time. Please try again later.",
          toast: false,
          sticky: false,
          dismissRoute: ['my-orders-view'],
        } as Message;
        actionsToDispatch.push(upsertMessage({ message }));
        setTimeout(() => {
          this.messageHandlerService.dismissSpinnerModal();
        }, 250);
        return actionsToDispatch;
      }),
      switchMap(actions => actions),
    ),
  );

  clientConvertToWillCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.convertToWillCall),
      map(payload => {
        const actionsToDispatch = [];
        if (payload) {
          if (payload.isEditWillCallFlow) {
            actionsToDispatch.push(
              CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceEditWillCallSubmissionApiRequest(
                {
                  tracking: {
                    ...payload.trackingData,
                    tracing: {
                      ...payload.trackingData.tracing,
                      transformFunc: genericTracingTransformer,
                      data: {
                        ...payload.trackingData.tracing.data,
                        isStartOfTrace: true,
                        isEndOfTrace: false,
                      },
                    },
                  },
                },
              ),
            );
          } else {
            actionsToDispatch.push(
              CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceSubmitWillCallFormApiRequest(
                {
                  tracking: {
                    ...payload.trackingData,
                    tracing: {
                      ...payload.trackingData.tracing,
                      transformFunc: genericTracingTransformer,
                      data: {
                        ...payload.trackingData.tracing.data,
                        isStartOfTrace: false,
                        isEndOfTrace: false,
                      },
                    },
                  },
                },
              ),
            );
          }
          return actionsToDispatch;
        }
      }),
      switchMap(actions => actions),
    ),
  );

  clientConvertToWillCallAsync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.convertToWillCallAsync),
      mergeMap(action => {
        const orderItems = this.orderItemDtoService.toClientModelArray(
          action?.orderTransactions[0]?.orderItems,
        );
        return combineLatest([
          of(action),
          this.orderService.getProductsForTracing$(orderItems),
        ]).pipe(take(1));
      }),
      map(([payload, productMap]) => {
        const actionsToDispatch = [];
        if (payload) {
          const newOrder = payload?.orderTransactions[0]; //websocket order
          const webSocketOrderItems =
            this.orderItemDtoService.toClientModelArray(newOrder?.orderItems);
          const webSocketOrderProducts =
            SharedTrackingService.tracingProductData(
              webSocketOrderItems,
              productMap,
            );
          if (!newOrder?.errorDetails) {
            const message = {
              id: this.uuid(),
              stack: null,
              watermark: new Date().toUTCString(),
              read: false,
              type: MessageTypeEnum.success,
              display: ToastMessages.convertToWillCallWebSocketSuccess,
              toast: true,
              sticky: false,
              duration: 3000,
            } as Message;
            actionsToDispatch.push(upsertMessage({ message }));
          }
          const trackingData = {
            tracing: {
              data: {
                isEndOfTrace: true,
                isStartOfTrace: false,
                traceContext: ConvertToWillCallTracingMessages.traceContext,
                attributes: {
                  event:
                    ConvertToWillCallTracingMessages.submitWillCallOrderEvent,
                  order: {
                    orderId: newOrder?.orderId,
                    tandemOrderNumber: newOrder?.tandemOrderNumber,
                    origOrderId: !!newOrder?.origOrderId
                      ? newOrder.origOrderId
                      : '',
                    origSplitOrderId: !!newOrder?.origSplitOrderId
                      ? newOrder.origSplitOrderId
                      : '',
                    requestedDeliveryDate: newOrder?.requestedDeliveryDate,
                    confirmedDeliveryDate: newOrder?.confirmedDeliveryDate,
                    totalAmount: newOrder?.totalDollars,
                    pickUpDateTime: newOrder?.willCallDateTime,
                    pickUpPersonName: newOrder?.willCallPersonName,
                  },
                  webSocketOrderProducts: webSocketOrderProducts,
                  errorDetails: !!newOrder.errorDetails
                    ? newOrder.errorDetails
                    : {},
                },
              },
              transformFunc: genericTracingTransformer,
            },
          } as Tracking;
          actionsToDispatch.push(
            CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceSubmitWillCallWebSocketResponseSuccess(
              {
                tracking: trackingData,
              },
            ),
          );
          this.orderService.stopWillCallWebSocketTimer();
        }
        return actionsToDispatch;
      }),
      switchMap(actions => actions),
    ),
  );

  clientConvertToWillCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.convertToWillCallFail),
      map(payload => {
        this.orderService.stopWillCallWebSocketTimer();
        const actionsToDispatch = [];
        if (payload) {
          const message = {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            display:
              payload.error?.message ||
              'Sorry, the request could not be processed. Please try again later.',
            toast: false,
            sticky: false,
          } as Message;
          const tracking: Tracking = {
            tracing: {
              data: {
                isEndOfTrace: true,
                isStartOfTrace: false,
                traceContext: ConvertToWillCallTracingMessages.traceContext,
                attributes: {
                  event:
                    ConvertToWillCallTracingMessages.submitWillCallOrderEvent,
                  error: !!payload ? payload : {},
                },
              },
              transformFunc: genericTracingTransformer,
            },
          };
          actionsToDispatch.push(upsertMessage({ message }));
          actionsToDispatch.push(
            CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceSubmitWillCallFormApiFailed(
              { tracking },
            ),
          );
          this.orderConversionService.setWillCallInFlight(false);
        }
        return actionsToDispatch;
      }),
      switchMap(actions => actions),
    ),
  );

  clientConvertToWillCallAsyncFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.convertToWillCallAsyncFail),
      map(payload => {
        this.orderService.stopWillCallWebSocketTimer();
        const actionsToDispatch = [];
        if (payload) {
          const message = {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            display:
              payload.error?.message ||
              "We're sorry but your order could not be converted to will call, please try again.",
            toast: true,
            sticky: true,
          } as Message;
          actionsToDispatch.push(upsertMessage({ message }));
        }
        const trackingData = {
          tracing: {
            data: {
              isEndOfTrace: true,
              isStartOfTrace: false,
              traceContext: ConvertToWillCallTracingMessages.traceContext,
              attributes: {
                event:
                  ConvertToWillCallTracingMessages.submitWillCallOrderEvent,
              },
            },
            transformFunc: genericTracingTransformer,
          },
        } as Tracking;
        actionsToDispatch.push(
          CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceSubmitWillCallWebSocketResponseFail(
            {
              tracking: trackingData,
            },
          ),
        );
        return actionsToDispatch;
      }),
      switchMap(actions => actions),
    ),
  );

  clientConvertToWillCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.convertToWillCallSuccess),
      map(payload => {
        this.orderService.startWillCallWebSocketTimer(payload?.orderHeader);
        const actionsToDispatch = [];
        if (!payload?.orderHeader || !!payload.orderHeader.errorDetails) {
          const message = {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            display:
              'The order has been edited or submitted by another user, please review the order.',
            toast: false,
            sticky: false,
          } as Message;
          actionsToDispatch.push(upsertMessage({ message }));
          //MyOrdersAction for willcallInFlight will be replaced by new implementation once it is applied throughout the app
          actionsToDispatch.push(
            MY_ORDERS_ACTIONS.setMyOrdersWillCallInFlight({
              willCallInFlight: false,
            }),
          );
          this.orderConversionService.setWillCallInFlight(false);
        }

        const tracking: Tracking = {
          tracing: {
            data: {
              isEndOfTrace: false,
              isStartOfTrace: false,
              traceContext: ConvertToWillCallTracingMessages.traceContext,
              attributes: {
                event:
                  ConvertToWillCallTracingMessages.submitWillCallOrderEvent,
                order: {
                  orderId: payload?.orderHeader?.orderId,
                  tandemOrderNumber: payload?.orderHeader?.tandemOrderNumber,
                  pickUpDateTime: payload?.orderHeader?.willCallDateTime,
                  pickUpPersonName: payload?.orderHeader?.willCallPersonName,
                },
                errorDetails: !!payload?.orderHeader?.errorDetails
                  ? payload.orderHeader.errorDetails
                  : {},
              },
            },
            transformFunc: genericTracingTransformer,
          },
        };
        actionsToDispatch.push(
          CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceSubmitWillCallFormApiSuccess(
            {
              tracking,
            },
          ),
        );
        this.orderConversionService.setWillCallInFlight(false);
        return actionsToDispatch;
      }),
      switchMap(actions => actions),
    ),
  );
  clientUpdateShipToDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SHIP_TO_ACTIONS.updateShipToDetails),
        tap(_ => {
          this.orderConversionService.setShipToInFlight(true);
        }),
      ),
    { dispatch: false },
  );

  clientUpdateShipToDetailsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SHIP_TO_ACTIONS.updateShipToDetailsSuccess),
        tap(payload => {
          if (!!payload.order?.orderHeader?.errorDetails) {
            sessionStorage.removeItem('shipToConversionTrace');
            this.orderConversionService.showShipToErrorToast();
          }
        }),
      ),
    { dispatch: false },
  );
  clientUpdateShipToDetailsAsyncFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MY_ORDERS_ACTIONS.updateShipToDetailsAsyncFail),
        tap(_ => {
          this.orderConversionService.showShipToErrorToast();
        }),
      ),
    { dispatch: false },
  );

  clientModifyOrderButtonClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.modifyOrderButtonClick),
        switchMap(payload => {
          return this.myOrdersStoreService
            .getOrderWithId(payload?.orderId)
            .pipe(
              filter(order => !!order),
              take(1),
              switchMap(order => {
                return combineLatest([
                  of(order),
                  this.panamax.currentUrl$.pipe(
                    take(1),
                    map(url => url?.split('/')[2] === 'my-orders'),
                  ),
                  this.orderService.getProductsForTracing$(order?.orderItems),
                ]).pipe(take(1));
              }),
            );
        }),
        tap(([order, isOrderStatusPage, productMap]) => {
          this.orderTracingService.traceEditOrderButtonClicked(
            order?.orderHeader?.orderId,
            order?.orderHeader?.origOrderId,
            order?.orderHeader?.origSplitOrderId,
            order?.orderHeader?.totalPrice,
            order?.orderHeader?.tandemOrderNumber,
            order?.orderHeader?.customerNumber,
            order?.orderHeader?.departmentNumber,
            order?.orderHeader?.divisionNumber,
            order?.orderHeader?.requestedDeliveryDate,
            order?.orderHeader?.confirmedDeliveryDate,
            isOrderStatusPage,
          );

          const analyticsOrderId = OrderAnalyticsService.getAnalyticsOrderId(
            order?.orderHeader,
          );

          this.orderSubmittedAnalyticsService.trackEditOrderButtonClick(
            order?.orderItems,
            productMap,
            analyticsOrderId,
            order?.orderHeader?.orderStatus,
          );
        }),
      ),
    { dispatch: false },
  );

  appForeground$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PLATFORM_ACTIONS.appForeground),
      withLatestFrom(this.store.select(orderHeaderSelectors.selectOrderHeader)),
      switchMap(([action, currentOrderHeader]) => {
        console.log(
          'App Foreground event: time in background  ',
          action.timeInBackground,
        );
        const thirtyMinutes = 30 * 60 * 1000;
        let actionsToExecute = [];

        if (action.timeInBackground >= MAX_TIME_IN_BACKGROUND) {
          actionsToExecute = [
            MY_ORDERS_ACTIONS.getInProgressOrdersContext(),
            NEXT_DELIVERY_ACTIONS.getDeliveryInfo(),
            MY_ORDERS_ACTIONS.getCustomerOrders(),
            ORDER_REVIEW_ACTIONS.getOrderReviewPreferences(),
          ];
        }
        if (
          action.timeInBackground >= thirtyMinutes &&
          currentOrderHeader?.orderId
        ) {
          actionsToExecute.push(
            MY_ORDERS_ACTIONS.checkOrderStatus({
              orderId: currentOrderHeader.orderId,
            }),
          );
        }
        if (actionsToExecute.length > 0) {
          return actionsToExecute;
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  //TODO: most of this logic should be extracted to a service, this is way too much code for an effect
  openAddToOrderModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.openAddToOrderModal),
        withLatestFrom(this.store.select(partnerSelectors.selectPartner)),
        switchMap(([response, partnerState]) => {
          if (this.modalOpen) {
            return EMPTY; // Skip opening the modal
          }
          // Set the modalOpen flag to true
          this.modalOpen = true;
          let newItemDollars =
            response?.modalData?.updatedOrderHeader?.totalDollars;
          if (this.platformService.platformType === PlatformEnum.desktop) {
            this.modalService.setModalOptions(
              false,
              ModalWithNavComponent,
              {
                rootPage: AddToOrderModalDesktopTabletComponent,
                rootPageParams: {
                  orderHeader: response?.modalData?.updatedOrderHeader,
                  nextDeliveryDate: response.modalData?.nextDeliveryDate,
                  updateOrderItem:
                    response.modalData?.updateOrderItemWithSequence,
                  origOrderItems: response.modalData?.origOrderItemsClone,
                  disablePO: !!partnerState?.partnerId,
                },
              },
              'modal-desktop-size-xl',
              false,
            );
          } else if (
            this.platformService.platformType === PlatformEnum.tablet
          ) {
            this.modalService.setModalOptions(true, ModalWithNavComponent, {
              rootPage: AddToOrderModalDesktopTabletComponent,
              rootPageParams: {
                orderHeader: response?.modalData?.updatedOrderHeader,
                nextDeliveryDate: response.modalData?.nextDeliveryDate,
                updateOrderItem:
                  response.modalData?.updateOrderItemWithSequence,
                origOrderItems: response.modalData?.origOrderItemsClone,
                disablePO: !!partnerState?.partnerId,
              },
            });
          } else {
            //Mobile only
            this.modalService.setModalOptions(true, ModalWithNavComponent, {
              rootPage: AddToOrderModalMobileComponent,
              rootPageParams: {
                orderHeader: response?.modalData?.updatedOrderHeader,
                nextDeliveryDate: response.modalData?.nextDeliveryDate,
                updateOrderItem:
                  response.modalData?.updateOrderItemWithSequence,
                origOrderItems: response.modalData?.origOrderItemsClone,
                disablePO: !!partnerState?.partnerId,
              },
            });
          }

          const modal = this.modalController.create(
            this.modalService.modalOptions,
          );

          return from(modal).pipe(
            map(modal => ({ modal, newItemDollars, response })),
          );
        }),
        switchMap(({ modal, newItemDollars, response }) => {
          modal.present();
          return modal.onDidDismiss().then(data => {
            this.modalOpen = false; // Reset the flag when the modal is dismissed
            if (
              !data.data ||
              (data.role !== undefined &&
                (data.role === 'gesture' ||
                  data.role === 'backdrop' ||
                  data.role === 'cancel'))
            ) {
              this.orderStoreService.resetOrder();
              this.clearQuantityUpdateInputBox();
            } else {
              //valid dismiss event like next or submit
              let totalDollars =
                data?.data?.currentTab === 1
                  ? newItemDollars
                  : data?.data?.orderHeader?.totalDollars + newItemDollars;
              if (response?.modalData?.trackingData) {
                this.orderAnalyticsService.trackAddToOrder(
                  response.modalData.trackingData,
                  response.modalData.updatedOrderHeader,
                  totalDollars,
                  response.modalData.origOrderItemsClone,
                  response.modalData.updateOrderItemWithSequence,
                );
              }
              this.orderStoreService.updateOrder(
                { ...data?.data?.orderHeader, totalDollars: totalDollars },
                data.data?.origOrderItems,
                data.data?.updateOrderItem,
              );
            }
          });
        }),
      ),
    { dispatch: false },
  );

  submitAddToOrderModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.dismissAddToOrderModal),
        tap(res => {
          this.modalController.dismiss(res.modalData);
        }),
      ),
    { dispatch: false },
  );

  removeBadOrderFromCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.removeBadOrderFromCart),
      switchMap(() => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            toast: false,
            title: 'Cart Cleared',
            display:
              'There was an issue with your cart. Please create a new order.',
          },
        }),
      ]),
    ),
  );

  printOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.printSubmittedOrder),
        mergeMap(action =>
          combineLatest([
            of(action),
            this.store.select(productWithAlternativesSelector),
            this.store.select(selectedCustomer),
          ]).pipe(take(1)),
        ),
        tap(([payload, productMap, customer]) => {
          const filePrefix = payload?.orderConfirmation
            ? 'Order_Confirmation'
            : `Submitted_Order${payload?.orders[0]?.orderHeader?.tandemOrderNumber}`;

          this.orderPrintService.printSubmittedOrder(
            payload?.orders,
            productMap,
            customer,
            filePrefix,
            payload.orderTallies,
            payload?.orderMinimum,
            payload?.shipToCustomer,
            payload?.shipToDepartmentName,
            payload?.orderStatusDetails,
          );
        }),
      ),
    { dispatch: false },
  );

  printCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.printCart),
        mergeMap(action =>
          combineLatest([
            of(action),
            this.orderService.loadOrderViewModel$(),
            this.store.select(selectedCustomer),
            this.orderService.loadOrderMinimumViewModel$(),
            this.orderService.selectedCustomerProntoDeliveryDays(),
          ]).pipe(take(1)),
        ),
        tap(([payload, orderVm, customer, orderMinVm, prontoDeliveryDays]) => {
          const expectedOrders = orderVm?.orderTallies?.totalShipments;
          const tracking = printOrderAnalytics(
            orderVm?.orderHeader.orderId,
            null,
            expectedOrders,
          );
          this.store.dispatch(
            ORDER_ANALYTICS_ACTIONS.trackPrintOrderInCart({
              tracking,
            }),
          );
          this.orderPrintService.printCart(
            orderVm?.orderHeader,
            orderVm?.shipments,
            customer,
            orderMinVm,
            prontoDeliveryDays,
          );
        }),
      ),
    { dispatch: false },
  );

  shareCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.shareCart),
        mergeMap(action =>
          combineLatest([
            of(action),
            this.orderService.loadOrderViewModel$(),
          ]).pipe(take(1)),
        ),
        tap(([_payload, orderVm]) => {
          const getOrderItems = (ids: any[], entities: {}): OrderItem[] =>
            ids && entities ? ids.map(id => entities[id]) : undefined;
          // Order share analytics goes here

          this.orderShareService.shareCart({
            orderHeader: orderVm.orderHeader,
            orderItems: getOrderItems(
              orderVm.orderItemState?.ids,
              orderVm.orderItemState?.entities,
            ),
          });
        }),
      ),
    { dispatch: false },
  );

  shareSubmittedOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.shareSubmittedOrder),
        tap(({ orders }) => {
          // Order share analytics goes here

          this.orderShareService.shareSubmittedOrder(orders);
        }),
      ),
    { dispatch: false },
  );

  shareOrderConfirmation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.shareOrderConfirmation),
        tap(({ orders }) => {
          // Order share analytics goes here

          this.orderShareService.shareOrderConfirmation(orders);
        }),
      ),
    { dispatch: false },
  );

  downloadCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.downloadCart),
        mergeMap(action =>
          combineLatest([
            of(action),
            this.store.select(productWithAlternativesSelector),
          ]).pipe(take(1)),
        ),
        tap(([{ orders, fileName, fileType }, products]) => {
          this.orderDownloadService.downloadAndOpenOrder(orders, {
            fileName,
            fileType,
            products,
            isCart: true,
          });
        }),
      ),
    { dispatch: false },
  );

  punchoutLogoutReady$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ORDER_PUNCHOUT_ACTIONS.punchoutLogoutSuccess,
          ORDER_PUNCHOUT_ACTIONS.punchoutLogoutFail,
        ),
        tap(() => {
          this.authConnectService.logout();
        }),
      ),
    { dispatch: false },
  );

  getCurrentPath(): string {
    return window.location.pathname;
  }

  uuid() {
    return uuidv4();
  }

  /**
   * Method will query on DOM having 'has-value' ionic input class
   * and will reset the values for current viewport.
   *
   * Scope: Useful in scenario where NgRx effect does not have a hold on template and you want to clear the ionic quantity box
   */
  clearQuantityUpdateInputBox() {
    const inputElements = document.querySelectorAll('.has-value input');
    inputElements.forEach(el => {
      if (
        el instanceof HTMLInputElement &&
        el.value.length &&
        !el.value.includes('.') &&
        Number(el.value) > 0
      ) {
        el.value = '';

        el.dispatchEvent(new Event('input')); // Trigger input event to update the value
        el.blur(); // Explicitly remove focus
      }
    });
  }

  constructor(
    private actions$: Actions,
    private orderService: OrderService,
    private panamax: PanAppState,
    private router: Router,
    private messageHandlerService: MessageHandlerService,
    private myOrdersStoreService: MyOrdersStoreService,
    private toastService: ToastService,
    readonly platformService: PlatformService,
    private homeService: HomeService,
    private orderItemDtoService: OrderItemDTOService,
    private store: Store,
    private orderStoreService: OrderStoreService,
    private modalService: ModalService,
    private modalController: ModalController,
    private orderAnalyticsService: OrderAnalyticsService,
    private orderPrintService: OrderPrintService,
    private orderDownloadService: OrderDownloadService,
    orderShareViaEmailService: OrderShareViaEmailService,
    orderShareViaNativeService: OrderShareViaNativeService,
    private authConnectService: AuthConnectService,
    private translateService: TranslateService,
    private orderConversionService: OrderConversionService,
    private orderSubmittedAnalyticsService: OrderSubmittedAnalyticsService,
    private orderSubmittedTracingService: OrderSubmittedTracingService,
    private orderTracingService: OrderTracingService,
  ) {
    this.orderShareService = [
      orderShareViaEmailService,
      orderShareViaNativeService,
    ].find((shareService: OrderShareService) =>
      shareService.isSupported(platformService.getPlatformType()),
    );
  }
}
